// Keyboard Shortcut Provider (E007-S021) - Global keyboard shortcut management
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ShortcutHelpModal } from '../components/modals/ShortcutHelpModal';
import { useKeyboardShortcuts, commonShortcuts, campaignListShortcuts } from '../hooks/useKeyboardShortcuts';

interface KeyboardShortcutContextType {
  commandMode: string | null;
  showHelpModal: () => void;
  hideHelpModal: () => void;
  isHelpModalOpen: boolean;
}

const KeyboardShortcutContext = createContext<KeyboardShortcutContextType | null>(null);

export const useKeyboardShortcutContext = () => {
  const context = useContext(KeyboardShortcutContext);
  if (!context) {
    throw new Error('useKeyboardShortcutContext must be used within KeyboardShortcutProvider');
  }
  return context;
};

interface KeyboardShortcutProviderProps {
  children: ReactNode;
}

export const KeyboardShortcutProvider: React.FC<KeyboardShortcutProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [commandMode, setCommandMode] = useState<string | null>(null);
  const [commandModeTimeout, setCommandModeTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

  // Show command mode indicator
  const showHelpModal = useCallback(() => setIsHelpModalOpen(true), []);
  const hideHelpModal = useCallback(() => setIsHelpModalOpen(false), []);

  // Handle two-key command mode (G+D, G+C, etc.)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip if in input
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.getAttribute('contenteditable') === 'true'
      ) {
        return;
      }

      // If in command mode, check for second key
      if (commandMode === 'g') {
        let navigated = false;
        
        switch (event.key.toLowerCase()) {
          case 'd':
            navigate('/dashboard');
            navigated = true;
            break;
          case 'c':
            navigate('/campaigns');
            navigated = true;
            break;
          case 'r':
            navigate('/reports');
            navigated = true;
            break;
          case 's':
            navigate('/settings');
            navigated = true;
            break;
          case 'a':
            navigate('/alerts');
            navigated = true;
            break;
          case 'b':
            navigate('/boards');
            navigated = true;
            break;
          case 'l':
            navigate('/creative-library');
            navigated = true;
            break;
        }
        
        if (navigated) {
          event.preventDefault();
        }
        
        // Exit command mode
        setCommandMode(null);
        if (commandModeTimeout) {
          clearTimeout(commandModeTimeout);
          setCommandModeTimeout(null);
        }
        return;
      }

      // Enter command mode when 'g' is pressed
      if (event.key.toLowerCase() === 'g' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        setCommandMode('g');
        
        // Auto-exit after 2 seconds
        const timeout = setTimeout(() => {
          setCommandMode(null);
        }, 2000);
        setCommandModeTimeout(timeout);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [commandMode, commandModeTimeout, navigate]);

  // Listen for custom events
  useEffect(() => {
    const handleShowHelp = () => setIsHelpModalOpen(true);
    const handleCloseModal = () => setIsHelpModalOpen(false);
    const handleRefreshData = () => {
      // Dispatch a custom event that pages can listen to
      window.dispatchEvent(new CustomEvent('doRefreshData'));
    };

    window.addEventListener('showShortcutHelp', handleShowHelp);
    window.addEventListener('closeModal', handleCloseModal);
    window.addEventListener('refreshData', handleRefreshData);

    return () => {
      window.removeEventListener('showShortcutHelp', handleShowHelp);
      window.removeEventListener('closeModal', handleCloseModal);
      window.removeEventListener('refreshData', handleRefreshData);
    };
  }, []);

  // Register common shortcuts
  useKeyboardShortcuts(commonShortcuts);

  // Register campaign list shortcuts only on campaign pages
  const isCampaignPage = location.pathname.includes('/campaign');
  useKeyboardShortcuts(isCampaignPage ? campaignListShortcuts : []);

  return (
    <KeyboardShortcutContext.Provider
      value={{
        commandMode,
        showHelpModal,
        hideHelpModal,
        isHelpModalOpen,
      }}
    >
      {children}

      {/* Command Mode Indicator */}
      {commandMode && (
        <div className="fixed bottom-4 right-4 z-50 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 animate-pulse">
          <kbd className="px-2 py-1 bg-gray-700 rounded text-sm font-mono">
            {commandMode.toUpperCase()}
          </kbd>
          <span className="text-sm text-gray-300">
            Press: D (Dashboard), C (Campaigns), R (Reports), S (Settings)
          </span>
        </div>
      )}

      {/* Help Modal */}
      <ShortcutHelpModal isOpen={isHelpModalOpen} onClose={hideHelpModal} />
    </KeyboardShortcutContext.Provider>
  );
};

export default KeyboardShortcutProvider;
